import { useEffect, useState } from 'react';
import { Banknote, Plus, Wallet } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, Separator, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useCompanyOptions } from '@/features/crm/api';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useBankAccounts, useOpenInvoices, usePayments, useRecordPayment } from './api';
import { PAYMENT_METHOD_LABELS, addAmounts } from './labels';
import type { Payment, PaymentMethod } from './types';

const METHODS = Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[];

export function PaymentsPage() {
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  const [method, setMethod] = useState('');
  const [recording, setRecording] = useState(false);

  const debounced = useDebounce(search);
  const { data, isLoading, error, refetch } = usePayments({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    method: method || undefined,
  });

  const columns: Column<Payment>[] = [
    {
      key: 'number',
      header: 'Payment',
      cell: (row) => (
        <div className="min-w-0">
          <div className={cn('font-medium', row.isRefund && 'text-destructive')}>
            {row.number}
            {row.isRefund && <span className="ms-1 text-xs">(refund)</span>}
          </div>
          <div className="truncate text-xs text-muted-foreground">{row.company.name}</div>
        </div>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => formatDate(row.date),
    },
    {
      key: 'amount',
      header: 'Amount',
      className: 'tabular text-sm font-medium',
      cell: (row) => formatMoney(row.amount, row.currency),
    },
    {
      key: 'method',
      header: 'Method',
      className: 'text-sm text-muted-foreground',
      cell: (row) => (
        <span>
          {PAYMENT_METHOD_LABELS[row.method]}
          {row.chequeStatus && (
            <Badge variant="secondary" className="ms-1.5 text-[10px]">
              {row.chequeStatus}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'applied',
      header: 'Applied to',
      className: 'text-sm',
      cell: (row) =>
        row.allocations.length === 0 ? (
          <span className="text-muted-foreground">Unapplied</span>
        ) : (
          <span className="text-xs">
            {row.allocations.map((allocation) => allocation.invoice.number).join(', ')}
          </span>
        ),
    },
    {
      key: 'unallocated',
      header: 'On account',
      className: 'tabular text-sm',
      cell: (row) =>
        row.unallocatedAmount === '0.00' ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="font-medium text-amber-600">
            {formatMoney(row.unallocatedAmount, row.currency)}
          </span>
        ),
    },
    {
      key: 'reference',
      header: 'Reference',
      className: 'text-sm text-muted-foreground',
      cell: (row) => row.reference ?? '—',
    },
  ];

  return (
    <div>
      <PageHeader
        title="Payments"
        description="Money received, and which invoices it settles. Anything unapplied stays as client credit."
        actions={
          <Can permission="finance.payment.create">
            <Button onClick={() => setRecording(true)}>
              <Plus className="h-4 w-4" />
              Record payment
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          placeholder="Search number, reference or company…"
          className="h-9 w-full sm:max-w-xs"
        />
        <Select
          value={method}
          onChange={(event) => {
            setMethod(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[10rem]"
        >
          <option value="">All methods</option>
          {METHODS.map((value) => (
            <option key={value} value={value}>
              {PAYMENT_METHOD_LABELS[value]}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load payments.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        emptyTitle="No payments yet"
        emptyDescription="Record the first one when a client pays."
        emptyAction={
          <Can permission="finance.payment.create">
            <Button onClick={() => setRecording(true)}>
              <Banknote className="h-4 w-4" />
              Record payment
            </Button>
          </Can>
        }
      />

      {recording && <RecordPaymentDialog onClose={() => setRecording(false)} />}
    </div>
  );
}

/**
 * Recording a payment and applying it are one step, because that is how the
 * work actually happens: money arrives against specific invoices.
 */
function RecordPaymentDialog({ onClose }: { onClose: () => void }) {
  const record = useRecordPayment();
  const { data: companies = [] } = useCompanyOptions();
  const { data: bankAccounts = [] } = useBankAccounts();

  const [companyId, setCompanyId] = useState('');
  const { data: openInvoices = [] } = useOpenInvoices(companyId || undefined);

  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<PaymentMethod>('BANK_TRANSFER');
  const [bankAccountId, setBankAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeBank, setChequeBank] = useState('');
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const currency = companies.find((company) => company.id === companyId)?.currency ?? 'AED';

  // Default the bank account once they load — most payments go to one place.
  useEffect(() => {
    if (!bankAccountId && bankAccounts.length > 0) setBankAccountId(bankAccounts[0].id);
  }, [bankAccounts, bankAccountId]);

  const allocatedTotal = Object.values(allocations).reduce(
    (sum, value) => addAmounts(sum, value || '0'),
    '0.00',
  );
  const unallocated = addAmounts(amount || '0', `-${allocatedTotal}`);

  /** Applies the payment to the oldest invoices first, which is the usual rule. */
  const autoApply = () => {
    let remaining = amount || '0';
    const next: Record<string, string> = {};
    for (const invoice of openInvoices) {
      if (remaining === '0.00' || remaining.startsWith('-')) break;
      const due = invoice.balanceDue ?? '0';
      const apply = compare(remaining, due) >= 0 ? due : remaining;
      next[invoice.id] = apply;
      remaining = addAmounts(remaining, `-${apply}`);
    }
    setAllocations(next);
  };

  const submit = async () => {
    setError(null);
    try {
      await record.mutateAsync({
        companyId,
        amount,
        date,
        method,
        bankAccountId,
        reference,
        notes,
        ...(method === 'CHEQUE' ? { chequeNumber, chequeBank, chequeStatus: 'RECEIVED' } : {}),
        allocations: Object.entries(allocations)
          .filter(([, value]) => value && value !== '0.00')
          .map(([invoiceId, value]) => ({ invoiceId, amount: value })),
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not record the payment.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Record a payment</DialogTitle>
          <DialogDescription>
            Apply it to invoices below. Anything left over stays on the client account as credit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Company">
              <Select
                value={companyId}
                onChange={(event) => {
                  setCompanyId(event.target.value);
                  setAllocations({});
                }}
              >
                <option value="">Choose a company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Amount received">
              <Input
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </Field>
            <Field label="Date">
              <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
            </Field>
            <Field label="Method">
              <Select
                value={method}
                onChange={(event) => setMethod(event.target.value as PaymentMethod)}
              >
                {METHODS.map((value) => (
                  <option key={value} value={value}>
                    {PAYMENT_METHOD_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Into account">
              <Select
                value={bankAccountId}
                onChange={(event) => setBankAccountId(event.target.value)}
              >
                <option value="">Not banked</option>
                {bankAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Reference">
              <Input
                value={reference}
                placeholder="Transfer or receipt number"
                onChange={(event) => setReference(event.target.value)}
              />
            </Field>

            {method === 'CHEQUE' && (
              <>
                <Field label="Cheque number">
                  <Input
                    value={chequeNumber}
                    onChange={(event) => setChequeNumber(event.target.value)}
                  />
                </Field>
                <Field label="Drawn on">
                  <Input
                    value={chequeBank}
                    onChange={(event) => setChequeBank(event.target.value)}
                  />
                </Field>
              </>
            )}
          </div>

          {companyId && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Apply to invoices</Label>
                {openInvoices.length > 0 && amount && (
                  <button
                    onClick={autoApply}
                    className="text-xs text-link hover:underline"
                  >
                    Apply to oldest first
                  </button>
                )}
              </div>

              {openInvoices.length === 0 ? (
                <p className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
                  Nothing outstanding for this client. The payment will sit as credit on their
                  account.
                </p>
              ) : (
                <div className="scrollbar-thin max-h-56 space-y-1.5 overflow-y-auto rounded-md border p-2">
                  {openInvoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-sm">
                          {invoice.number}
                          {invoice.isOverdue && (
                            <Badge variant="destructive" className="text-[10px]">
                              Overdue
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Due {formatDate(invoice.dueDate)} ·{' '}
                          {formatMoney(invoice.balanceDue, invoice.currency)} outstanding
                        </div>
                      </div>
                      <Input
                        inputMode="decimal"
                        placeholder="0.00"
                        className="h-8 w-28 text-end"
                        value={allocations[invoice.id] ?? ''}
                        onChange={(event) =>
                          setAllocations((current) => ({
                            ...current,
                            [invoice.id]: event.target.value,
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              {amount && (
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Applied</span>
                    <span className="tabular">{formatMoney(allocatedTotal, currency)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-medium">
                    <span>Left on account</span>
                    <span
                      className={cn(
                        'tabular',
                        unallocated.startsWith('-') && 'text-destructive',
                      )}
                    >
                      {formatMoney(unallocated, currency)}
                    </span>
                  </div>
                  {unallocated.startsWith('-') && (
                    <p className="text-xs text-destructive">
                      You are applying more than the payment.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <Field label="Notes">
            <Textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
          </Field>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              record.isPending || !companyId || !amount || unallocated.startsWith('-')
            }
          >
            <Wallet className="h-4 w-4" />
            {record.isPending ? 'Recording…' : 'Record payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Compares two decimal-string amounts without going through a float. */
function compare(a: string, b: string): number {
  const toMinor = (value: string) => {
    const negative = value.startsWith('-');
    const [whole = '0', fraction = '00'] = value.replace('-', '').split('.');
    const minor = BigInt(whole) * 100n + BigInt((fraction + '00').slice(0, 2));
    return negative ? -minor : minor;
  };
  const left = toMinor(a);
  const right = toMinor(b);
  return left === right ? 0 : left > right ? 1 : -1;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
